// @ts-nocheck
import React, { useMemo } from 'react';
import Board from 'react-trello-19';
import { Provider } from 'react-redux';
import { StyleSheetManager } from 'styled-components';
import { store } from '../../store';
import { IBoard, ICard, ILane } from './models/BoardModel';

const TRELLO_DOM_FILTERED_PROPS = new Set([
  'handleDragEnd',
  'handleDragStart',
  'handleLaneDragEnd',
  'handleLaneDragStart',
  'eventBusHandle',
  'actions',
  'hideCardDeleteIcon',
  'collapsibleLanes',
  'cardDraggable',
  'laneDraggable',
  'cardDragClass',
  'laneDragClass',
]);

interface KanbanBoardProps<T> {
  initialBoard?: IBoard<T>;
  style?: React.CSSProperties;
  laneStyle?: React.CSSProperties;
  customCard?: (props: any) => React.ReactElement;
  customLaneHeader?: (props: any) => React.ReactElement;
  laneFooter?: (props: any) => React.ReactElement;
  customAddCardLink?: (props: any) => React.ReactElement;
  newCardForm?: (props: any) => React.ReactElement;
  newLaneSection?: (props: any) => React.ReactElement;
  newLaneForm?: (props: any) => React.ReactElement;
  handleOnCardDragEnd?: (
    cardId: string,
    sourceLandId: string,
    targetLaneId: string,
    position: number,
    card: ICard<T>
  ) => void;
  handleOnColumnDragEnd?: (removedIndex: string, addedIndex: string, payload: ILane<T>) => void;
  laneSortFunction?: (card1: ICard<T>, card2: ICard<T>) => void;
  eventBus?: (props: any) => void;
  /** Called by react-trello when a card is clicked (bypasses smooth-dnd drag interception) */
  onCardClick?: (cardId: string, metadata: any, laneId: string) => void;
}

// react-trello-19 renders custom components inside its own internal Redux
// <Provider>, so useTypedSelector inside them reads the wrong store.
// Wrap custom components that need app selectors to re-establish app store context.
// Card is intentionally not wrapped to keep smooth-dnd drag behavior stable.
const withAppStore = (Component) =>
  React.memo((props) => (
    <Provider store={store}>
      <Component {...props} />
    </Provider>
  ));

const KanbanBoard = <T,>({
  initialBoard,
  style,
  laneStyle,
  customCard = null,
  customLaneHeader = null,
  laneFooter = null,
  customAddCardLink = null,
  newCardForm = null,
  newLaneSection = null,
  newLaneForm = null,
  handleOnCardDragEnd,
  handleOnColumnDragEnd,
  laneSortFunction,
  eventBus,
  onCardClick,
}: KanbanBoardProps<T>) => {
  if (!initialBoard?.lanes) return <></>;

  // Memoize so we don't recreate component types on every render
  const components = useMemo(
    () => ({
      ...(customCard && { Card: customCard }),
      ...(customLaneHeader && { LaneHeader: withAppStore(customLaneHeader) }),
      ...(laneFooter && { LaneFooter: withAppStore(laneFooter) }),
      ...(customAddCardLink && { AddCardLink: withAppStore(customAddCardLink) }),
      ...(newCardForm && { NewCardForm: withAppStore(newCardForm) }),
      ...(newLaneSection && { NewLaneSection: withAppStore(newLaneSection) }),
      ...(newLaneForm && { NewLaneForm: withAppStore(newLaneForm) }),
    }),
    [
      customCard,
      customLaneHeader,
      laneFooter,
      customAddCardLink,
      newCardForm,
      newLaneSection,
      newLaneForm,
    ]
  );

  const editable =
    Boolean(customAddCardLink) ||
    Boolean(newCardForm) ||
    Boolean(newLaneSection) ||
    Boolean(newLaneForm);

  return (
    <StyleSheetManager shouldForwardProp={(prop) => !TRELLO_DOM_FILTERED_PROPS.has(prop)}>
      <Board
        style={style}
        laneStyle={laneStyle}
        data={initialBoard}
        components={components}
        editable={editable}
        canAddLanes={Boolean(newLaneForm)}
        handleDragEnd={handleOnCardDragEnd}
        handleLaneDragEnd={handleOnColumnDragEnd}
        laneSortFunction={laneSortFunction}
        draggable
        eventBusHandle={eventBus}
        onCardClick={onCardClick}
      />
    </StyleSheetManager>
  );
};

export default KanbanBoard;
